import { parseDate, toDateString } from "@taskfortress/core"
import React, { useCallback, useState } from "react"
import { cn } from "../../styles/utils"
import { Box } from "../Box"
import { DatePicker } from "../DatePicker"
import { Icon } from "../Icons"
import { Pressable } from "../Pressable"
import { FormControl } from "../gluestack/form-control"
import { Input, InputField } from "../gluestack/input"
import { FieldError } from "./FieldError"
import { FieldLabel } from "./FieldLabel"

type DateInputProps = {
  value?: Date
  handleChange: (value: Date) => void
  label?: string
  name: string
  required?: boolean
  className?: string
  inputContainerClassName?: string
  inputClassName?: string
  placeholder?: string
  disabled?: boolean
  hideError?: boolean
  error?: string
}

export const DateInput: React.FC<DateInputProps> = (props) => {
  const {
    value,
    handleChange,
    className,
    inputContainerClassName,
    inputClassName,
    hideError,
    placeholder,
    disabled,
    label,
    required,
    error,
  } = props

  const [isModalVisible, setIsModalVisible] = useState(false)

  const handleOpenModal = useCallback(() => {
    if (!disabled) {
      setIsModalVisible(true)
    }
  }, [disabled])

  const handleCloseModal = useCallback(() => {
    setIsModalVisible(false)
  }, [])

  const handleDateSelect = useCallback(
    (dateString: string) => {
      const parsedDate = parseDate(dateString)
      if (parsedDate.date) {
        handleChange(parsedDate.date)
        setIsModalVisible(false)
      }
    },
    [handleChange],
  )

  const displayValue = value ? toDateString(value) : ""

  return (
    <>
      <FormControl
        className={className}
        isInvalid={!!error}
        isRequired={required}
        isDisabled={disabled}
      >
        <FieldLabel label={label} required={required} />
        <Pressable onPress={handleOpenModal} disabled={disabled}>
          <Box>
            <Input
              pointerEvents="none"
              className={cn("relative", inputContainerClassName)}
            >
              <InputField
                className={cn("pr-8 flex-0", inputClassName)}
                value={displayValue}
                placeholder={placeholder || "YYYY-MM-DD"}
                editable={false}
              />
              <Box className="absolute left-24 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <Icon name="calendar" size="md" className="text-gray-500" />
              </Box>
            </Input>
          </Box>
        </Pressable>
        {!hideError && <FieldError error={error} />}
      </FormControl>

      <DatePicker
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        selectedDate={displayValue || toDateString(new Date())}
        onDateSelect={handleDateSelect}
        explicitSelectRequired={true}
        showTaskIndicators={true}
      />
    </>
  )
}
